import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg, fmtEur, fmtDate, waLink } from "@/lib/api";

const TL = { bono: "Bono", syntonic: "Syntonic", tomatis: "Tomatis", wivi: "Wivi", sesiones: "Sesiones" };
const CEN = { MADRID: "Madrid", ALCOBENDAS: "Alcobendas" };

export const CajaDia = ({ cobros }) => {
  const grupos = {};
  cobros.forEach((c) => {
    const k = c.patient.centro || "Sin centro";
    grupos[k] = grupos[k] || { total: 0, metodos: {} };
    grupos[k].total += c.importe;
    grupos[k].metodos[c.metodo || "—"] = (grupos[k].metodos[c.metodo || "—"] || 0) + c.importe;
  });
  const total = cobros.reduce((s, c) => s + c.importe, 0);
  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid="eco-caja">
      <div className="bg-[#1E3A8A] text-white rounded-lg p-4"><p className="text-[11px] uppercase tracking-wider opacity-80">Total del día</p><p className="text-3xl font-[Outfit] font-semibold" data-testid="eco-caja-total">{fmtEur(total)}</p><p className="text-xs opacity-80">{cobros.length} cobros</p></div>
      {Object.entries(grupos).map(([k, g]) => (
        <div key={k} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`eco-caja-${k}`}>
          <p className="text-[11px] uppercase tracking-wider text-slate-500">{CEN[k] || k}</p>
          <p className="text-2xl font-[Outfit] font-semibold text-slate-900">{fmtEur(g.total)}</p>
          {Object.entries(g.metodos).map(([m, v]) => <p key={m} className="flex justify-between text-xs text-slate-600"><span>{m}</span><span className="font-[JetBrains_Mono]">{fmtEur(v)}</span></p>)}
        </div>
      ))}
    </div>
  );
};

export const TablaCobros = ({ cobros, onChange }) => {
  const borrar = async (c) => {
    if (!window.confirm(`¿Borrar el cobro de ${fmtEur(c.importe)} de ${c.patient.nombre}?`)) return;
    try {
      await api.delete(`/eco/cobros/${c.id}`);
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
      <table className="w-full text-sm" data-testid="eco-cobros-table">
        <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50"><tr>{["Fecha", "Paciente", "Centro", "Concepto", "Forma", "Importe", "Factura", ""].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100">
          {cobros.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-400">No hay cobros en estas fechas.</td></tr>}
          {cobros.map((c) => (
            <tr key={c.id} data-testid={`eco-cobro-${c.id}`}>
              <td className="px-3 py-2 text-xs">{fmtDate(c.fecha)}</td>
              <td className="px-3 py-2"><Link to={c.treatment_id ? `/tratamiento/${c.treatment_id}` : `/pacientes/${c.patient_id}`} className="hover:underline">{c.patient.nombre}</Link></td>
              <td className="px-3 py-2 text-xs">{CEN[c.patient.centro] || ""}</td>
              <td className="px-3 py-2 text-xs">{[TL[c.tipo], c.concepto].filter(Boolean).join(" · ") || "—"}</td>
              <td className="px-3 py-2 text-xs">{c.metodo}</td>
              <td className="px-3 py-2 font-[JetBrains_Mono] text-xs">{fmtEur(c.importe)}</td>
              <td className="px-3 py-2 text-xs">{c.factura || <span className="text-slate-400">sin facturar</span>}</td>
              <td className="px-3 py-2">{c.origen === "suelto" && !c.factura && <button onClick={() => borrar(c)} className="text-rose-600" data-testid={`eco-cobro-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const Pendientes = ({ centro }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/eco/pendientes", { params: { centro } }).then((r) => setRows(r.data));
  }, [centro]);
  if (!rows) return null;
  const total = rows.reduce((s, r) => s + r.total, 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="eco-pendientes">
      <p className="px-4 py-3 text-sm text-slate-600 border-b">{rows.length} pacientes deben en total <b className="text-amber-700" data-testid="eco-pendientes-total">{fmtEur(total)}</b></p>
      <div className="divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.patient.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm" data-testid={`eco-pend-${r.patient.id}`}>
            <Link to={`/pacientes/${r.patient.id}`} className="flex-1 min-w-[180px] font-medium hover:underline">{r.patient.nombre}</Link>
            <span className="text-xs text-slate-500">{CEN[r.patient.centro] || ""}</span>
            <span className="text-xs text-slate-600">{r.tratamientos.map((t) => `${TL[t.tipo] || t.tipo} ${fmtEur(t.pendiente)}`).join(" · ")}</span>
            <b className="font-[JetBrains_Mono] text-amber-700">{fmtEur(r.total)}</b>
            {r.patient.telefono && <a href={waLink(r.patient.telefono, `Hola, os escribimos de Salud y Visión. Según nuestros registros queda pendiente un importe de ${fmtEur(r.total)} del tratamiento de ${r.patient.nombre}. Podéis abonarlo en vuestra próxima visita o por transferencia/Bizum. Si ya lo habéis pagado, decídnoslo y lo revisamos. Gracias.`)}
              target="_blank" rel="noreferrer" className="rounded bg-[#25D366] px-2 py-1 text-[11px] font-medium text-white">WhatsApp</a>}
          </div>
        ))}
      </div>
    </div>
  );
};
